import { Utils, P, Logging } from '@pacem/pacem-core';
import { Colors } from '@pacem/pacem-foundation';
import { Geometry } from '@pacem/pacem-numerical';
import { StalePropertyFlag, isMesh, isCamera, isLight, isGroup } from '../../types';
import { AdapterUtils } from '../shared';
import { LABEL_PREFIX, GPUTextureUsage } from './constants';
import { RenderPipelines } from './pipelinefactory';
import { Raycasters } from './raycaster';
import { RenderableBuffers, RenderLooper, ManagedBuffers } from './renderablebuffer';
//namespace Pacem.Drawing3D.WebGPU {
const Vector3D = Geometry.LinearAlgebra.Vector3D;
export var WebGPUIssueCause;
(function (WebGPUIssueCause) {
    WebGPUIssueCause[WebGPUIssueCause["NotSupported"] = 0] = "NotSupported";
    WebGPUIssueCause[WebGPUIssueCause["Disabled"] = 1] = "Disabled";
})(WebGPUIssueCause || (WebGPUIssueCause = {}));
export class Renderers {
    static create(stage, config) {
        return new Promise((resolve, reject) => {
            if (!navigator.gpu) {
                const message = "WebGPU not supported.";
                const issue = { message, cause: WebGPUIssueCause.NotSupported };
                reject(issue);
            }
            else {
                const retval = new RendererClass(stage);
                retval.init(config).then(_ => resolve(retval), error => {
                    const message = typeof error === 'string' ? error : error.message;
                    const issue = { message, cause: WebGPUIssueCause.Disabled };
                    reject(issue);
                });
            }
        });
    }
}
function getDefaultCamera(stage) {
    return {
        bottom: -1, top: 1, left: -1, right: 1, near: .1, far: 200, lookAt: Vector3D.from(0, 0, 0), up: Vector3D.j(), position: Vector3D.from(0, 0, 1), type: "orthographic",
        flags: [], stage
    };
}
function destroyTextures(...textures) {
    for (let tex of textures || []) {
        tex?.destroy();
    }
}
class RendererClass {
    #stage;
    #canvas;
    #context;
    #renderables;
    #geometries;
    #materials;
    #looper;
    #textures;
    #config;
    #disabled;
    // interaction buffer
    #interactionBuffer;
    // interaction buffer underlying data
    #interaction;
    constructor(stage) {
        this.#renderables = new WeakMap();
        this.#geometries = new WeakMap();
        this.#materials = new WeakMap();
        this.#config = { enableValidation: false };
        this.#disabled = false;
        this._logError = (evt) => {
            const stage = this.#stage, logger = stage.logger;
            if (!Utils.isNull(logger)) {
                const error = 'error' in evt ? evt.error : evt;
                logger.log(Logging.LogLevel.Error, error.message, 'WebGPU');
            }
        };
        this.#rebuildingContext = true;
        /** Whether the renderer is affected by vertex or fragment modifiers.
         * When true, the renderer must provide updated time, cursor position and canvas size to the pipeline's bind groups.
         */
        this.#hasModifiers = false;
        const wrapper = (this.#stage = stage).stage;
        const canvas = document.createElement('canvas');
        canvas.id = P + '_3d_stage_' + Utils.uniqueCode();
        // cleanup
        wrapper.innerHTML = '';
        wrapper.appendChild(this.#canvas = canvas);
        // interaction stuff
        this.#interactionBuffer = ManagedBuffers.create(this.#interaction = {
            time: performance.now(), cursor: { x: 0, y: 0 }, resolution: { width: 0, height: 0 }
        });
    }
    _buildPassDescriptor({ multisample, format }) {
        const { context, device } = this.#context;
        const canvasTexture = context.getCurrentTexture();
        let textures = this.#textures;
        if (Utils.isNull(textures)) {
            textures = {};
            if (multisample) {
                const renderTex = device.createTexture({
                    label: LABEL_PREFIX + 'render-texture',
                    format: format ?? canvasTexture.format,
                    usage: GPUTextureUsage.RENDER_ATTACHMENT,
                    size: [canvasTexture.width, canvasTexture.height],
                    sampleCount: 4,
                });
                textures.render = {
                    texture: renderTex, view: renderTex.createView({ label: LABEL_PREFIX + 'render-texture-view' })
                };
                const depthTex = device.createTexture({
                    label: LABEL_PREFIX + 'depth-texture',
                    format: 'depth24plus',
                    usage: GPUTextureUsage.RENDER_ATTACHMENT,
                    size: [canvasTexture.width, canvasTexture.height],
                    sampleCount: 4,
                });
                textures.depth = {
                    texture: depthTex, view: depthTex.createView({ label: LABEL_PREFIX + 'depth-texture-view' })
                };
            }
            this.#textures = textures;
        }
        const { transparent, background } = this.#stage;
        const backColor = transparent ? { r: 0, g: 0, b: 0, a: 0 } : background ? (typeof background === 'string' ? Colors.parse(background) : background) : { r: 0, g: 0, b: 0, a: 1 };
        const bgColor = AdapterUtils.flattenVectorArray([backColor]);
        const canvasTexView = canvasTexture.createView({ label: LABEL_PREFIX + 'canvas-texture-view' });
        const colorAttachment = {
            view: canvasTexView,
            clearValue: bgColor,
            loadOp: 'clear',
            storeOp: 'store',
        };
        const passDescriptor = {
            colorAttachments: [colorAttachment]
        };
        if (multisample) {
            const renderTexView = textures.render.view;
            const depthTexView = textures.depth.view;
            passDescriptor.depthStencilAttachment = {
                view: depthTexView,
                depthClearValue: 1,
                depthLoadOp: 'clear',
                depthStoreOp: 'store',
                stencilClearValue: 0,
                stencilReadOnly: true,
            };
            // override
            colorAttachment.view = renderTexView;
            colorAttachment.resolveTarget = canvasTexView;
        }
        return passDescriptor;
    }
    ;
    _buildContext(canvas = this.#canvas, size = this.#stage.size) {
        size ??= canvas;
        let w = size.width;
        let h = size.height;
        const devicePixelRatio = 1; // window.devicePixelRatio;
        canvas.width = w * devicePixelRatio;
        canvas.height = h * devicePixelRatio;
        const context = canvas.getContext('webgpu');
        const textureFormat = navigator.gpu.getPreferredCanvasFormat();
        return { context, textureFormat };
    }
    _buildUpContext(device, adapter, context, format) {
        const { transparent } = this.#stage;
        context.configure({
            alphaMode: transparent ? 'premultiplied' : 'opaque',
            device,
            format,
        });
        this.#context = { device, adapter, canvas: this.#canvas, context, format };
        this.#rebuildingContext = false;
    }
    async init(config) {
        this.#config = config;
        const adapter = await navigator.gpu.requestAdapter();
        if (Utils.isNull(adapter)) {
            throw new Error('WebGPU adapter unavalable or disabled.');
        }
        const device = await adapter.requestDevice();
        if (Utils.isNull(adapter)) {
            throw new Error('WebGPU device unavalable or disabled.');
        }
        device.addEventListener('uncapturederror', this._logError);
        // set flag for shader modifiers presence, this will be used to determine whether to update the interaction buffer on each render call
        this.#hasModifiers = !Utils.isNull(config.modifiers?.version) && !Utils.isNull(config.modifiers)
            && (!Utils.isNull(config.modifiers.vertexInput) || !Utils.isNull(config.modifiers.vertexOutput) || !Utils.isNull(config.modifiers.fragment));
        const { context, textureFormat: format } = this._buildContext();
        this._buildUpContext(device, adapter, context, format);
    }
    #rebuildingContext;
    /** Whether the renderer is affected by vertex or fragment modifiers.
     * When true, the renderer must provide updated time, cursor position and canvas size to the pipeline's bind groups.
     */
    #hasModifiers;
    /**
     * Render procedure.
     */
    render(interaction) {
        if (this.#rebuildingContext || Utils.isNull(this.#looper) || !(this.#canvas.width > 0) || !(this.#canvas.height > 0)) {
            return;
        }
        const context = this.#context, device = context.device, 
        // looper
        looper = this.#looper;
        // enable validation scope? (perf hog)
        const validationScope = this.#config?.enableValidation ?? false;
        if (this.#hasModifiers) {
            // update underlying interaction data, this will trigger buffer update
            Utils.extend(this.#interaction, interaction);
        }
        const multisample = true;
        const passDescriptor = this._buildPassDescriptor({ multisample });
        // rendering zone
        const commandEncoder = device.createCommandEncoder({ label: LABEL_PREFIX + 'renderer' });
        const pass = commandEncoder.beginRenderPass(passDescriptor);
        // loop
        looper.loop((mesh, camera, lights) => {
            if (mesh.item.hide) {
                // this code branching is NOT NEEDED ANYMORE,
                // remove it safely
                return false;
            }
            if (validationScope) {
                device.pushErrorScope('validation');
            }
            const renderables = [mesh, camera, ...lights];
            // inject here (pipeline options) any global rendering data (time, cursor, etc.) that may be needed by the shader modifiers
            const pipeline = RenderPipelines.create(context, renderables, { multisample: true, modifiers: this.#config.modifiers });
            // render
            pipeline.frame(pass, this.#interactionBuffer, ...renderables);
            if (validationScope) {
                device.popErrorScope().then(error => {
                    if (error) {
                        this._logError(error);
                    }
                });
            }
            return true;
        });
        pass.end();
        device.queue.submit([commandEncoder.finish()]);
    }
    set disabled(v) {
        this.#disabled = v;
    }
    get disabled() {
        return this.#disabled;
    }
    raycast(pixel) {
        if (this.#rebuildingContext || Utils.isNull(this.#looper)) {
            return Promise.resolve([]);
        }
        const context = this.#context, device = context.device, looper = this.#looper;
        // enable validation scope? (perf hog)
        const validationScope = this.#config?.enableValidation ?? false;
        // rendering zone
        const raycaster = Raycasters.createColorPicking(context);
        raycaster.begin();
        // loop
        looper.loop((mesh, camera) => {
            if (mesh.item.hide) {
                // TODO: remove unwraps and move to nested loops
                // in order to centralize hidden renderable skip logic
                return false;
            }
            if (validationScope) {
                device.pushErrorScope('validation');
            }
            raycaster.frame(this.#interactionBuffer, mesh, camera);
            if (validationScope) {
                device.popErrorScope().then(error => {
                    if (error) {
                        this._logError(error);
                    }
                });
            }
            return true;
        });
        return raycaster.end(pixel);
    }
    _disposeRenderableJustInCase(item) {
        const store = this.#renderables;
        if (store.has(item)) {
            store.get(item)?.destroy();
            store.delete(item);
        }
    }
    _createOrYieldRenderable(item, geometries = this.#geometries, materials = this.#materials) {
        item.stage ??= this.#stage;
        const store = this.#renderables;
        let found = store.get(item);
        if (Utils.isNull(found)) {
            store.set(item, found = RenderableBuffers.create(item, geometries));
        }
        return found;
    }
    _createOrYieldGeometry(item) {
        const store = this.#geometries;
        let found = store.get(item);
        if (Utils.isNull(found)) {
            store.set(item, found = ManagedBuffers.create(item));
        }
        return found;
    }
    _createOrYieldMaterial(item) {
        const store = this.#materials;
        let found = store.get(item);
        if (Utils.isNull(found)) {
            store.set(item, found = ManagedBuffers.create(item));
        }
        return found;
    }
    _unwrapBuffers(grp, accumulator) {
        accumulator ??= { meshes: [], lights: [], cameras: [], groups: [] };
        for (let itemBuffer of grp.children) {
            itemBuffer.item.stage ??= this.#stage;
            if (isMesh(itemBuffer.item)) {
                accumulator.meshes.push(itemBuffer);
            }
            if (isCamera(itemBuffer.item)) {
                accumulator.cameras.push(itemBuffer);
            }
            if (isLight(itemBuffer.item)) {
                accumulator.lights.push(itemBuffer);
            }
            if (isGroup(itemBuffer.item)) {
                accumulator.groups.push(itemBuffer);
                this._unwrapBuffers(itemBuffer, accumulator);
            }
        }
        return accumulator;
    }
    ;
    _update(array) {
        const groupBuffers = [];
        const meshBuffers = [];
        const lightBuffers = [];
        const factory = i => this._createOrYieldRenderable(i);
        let camera;
        for (let item of array) {
            if (item.hide) {
                // remove stored renderable just in case
                this._disposeRenderableJustInCase(item);
                continue;
            }
            // ensure stage
            item.stage ??= this.#stage;
            // groups
            if (isGroup(item)) {
                const g = item;
                if (g.flags.includes(StalePropertyFlag.Children)) {
                    this.#renderables.delete(g);
                }
                groupBuffers.push(factory(g));
            }
            // meshes
            if (isMesh(item) && item.geometry) {
                // build pipelines based on provided element wrappers (cached buffers)
                this._createOrYieldGeometry(item.geometry);
                if (!Utils.isNull(item.material)) {
                    this._createOrYieldMaterial(item.material);
                }
                meshBuffers.push(factory(item));
            }
            // lights
            if (isLight(item)) {
                lightBuffers.push(factory(item));
            }
            if (isCamera(item)) {
                // pick the first only
                camera ??= item;
            }
        }
        // find first active camera
        camera ??= getDefaultCamera(this.#stage);
        const cameraBuffer = factory(camera);
        let accumulator = {
            meshes: meshBuffers, lights: lightBuffers, cameras: [cameraBuffer], groups: groupBuffers
        };
        for (let groupBuffer of groupBuffers) {
            accumulator = this._unwrapBuffers(groupBuffer, accumulator);
        }
        // destroy leftovers
        this.#looper?.items.meshes.forEach(buffer => {
            if (!meshBuffers.includes(buffer)) {
                buffer.destroy();
            }
        });
        this.#looper = new RenderLooper(accumulator);
    }
    update(...items) {
        this._update(Array.from(items));
    }
    dispose() {
        this.#looper = null;
        if (this._disposeContext()) {
            const { device } = this.#context;
            device.removeEventListener('uncapturederror', this._logError);
            device.destroy;
        }
    }
    _disposeContext() {
        const ctx = this.#context;
        if (Utils.isNull(ctx)) {
            return false;
        }
        // dispose associated raycasters, if any
        Raycasters.dispose(ctx);
        // dispose associated pipelines, if any
        RenderPipelines.dispose(ctx);
        // dispose textures
        const textures = this.#textures;
        this.#textures = null;
        if (!Utils.isNull(textures)) {
            const { render, depth } = textures;
            destroyTextures(render?.texture, depth?.texture);
        }
        // dispose context
        ctx.context.unconfigure();
        return true;
    }
    resize(size) {
        this.#rebuildingContext = true;
        if (this._disposeContext()) {
            const { device, adapter } = this.#context;
            const { context, textureFormat: format } = this._buildContext(this.canvas, size);
            this._buildUpContext(device, adapter, context, format);
            // interaction
            if (this.#hasModifiers) {
                Utils.extend(this.#interaction, { resolution: size });
            }
        }
    }
    get canvas() {
        return this.#canvas;
    }
}
